import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CalendarPlus, Pencil, Trash2, X, CheckCircle2, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/visitor-access`;
const inp = "w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white";
const today = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const EMPTY = { date: today(), time_from: "", time_to: "", name: "", company: "", host: "", purpose: "", plate: "" };
const err = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : Array.isArray(d) ? "Vérifiez les champs saisis" : "Une erreur est survenue"; };
const fr = (d) => d.split("-").reverse().join("/");
const hm = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

function VisitForm({ token, edit, onDone, onCancel }) {
  const [f, setF] = useState(edit ? { ...EMPTY, ...Object.fromEntries(Object.entries(edit).map(([k, v]) => [k, v ?? ""])) } : EMPTY);
  const [msg, setMsg] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault(); setMsg("");
    const body = { date: f.date, time_from: f.time_from, time_to: f.time_to, name: f.name, company: f.company, host: f.host, purpose: f.purpose, plate: f.plate };
    try { if (edit) await axios.put(`${API}/${token}/${edit.id}`, body); else await axios.post(`${API}/${token}`, body); setF(EMPTY); onDone(); }
    catch (er) { setMsg(err(er)); }
  };
  return (
    <form onSubmit={save} className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3 shadow-sm" data-testid="public-visit-form">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-extrabold text-[#0B2A7A]">{edit ? "Modifier la visite" : "Annoncer un visiteur"}</h2>
        {edit && <button type="button" onClick={onCancel} className="p-1"><X className="h-5 w-5" /></button>}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <label className="text-xs font-semibold text-slate-600 col-span-2 sm:col-span-1">Date *<input data-testid="public-visit-date" type="date" required min={today()} value={f.date} onChange={set("date")} className={`${inp} mt-1`} /></label>
        <label className="text-xs font-semibold text-slate-600">De<input data-testid="public-visit-from" type="time" value={f.time_from} onChange={set("time_from")} className={`${inp} mt-1`} /></label>
        <label className="text-xs font-semibold text-slate-600">À<input data-testid="public-visit-to" type="time" value={f.time_to} onChange={set("time_to")} className={`${inp} mt-1`} /></label>
      </div>
      <input data-testid="public-visit-name" required value={f.name} onChange={set("name")} placeholder="Nom et prénom du visiteur *" className={inp} />
      <div className="grid sm:grid-cols-2 gap-2">
        <input data-testid="public-visit-company" value={f.company} onChange={set("company")} placeholder="Société du visiteur" className={inp} />
        <input data-testid="public-visit-host" value={f.host} onChange={set("host")} placeholder="Personne visitée" className={inp} />
        <input data-testid="public-visit-purpose" value={f.purpose} onChange={set("purpose")} placeholder="Motif de la visite" className={inp} />
        <input data-testid="public-visit-plate" value={f.plate} onChange={set("plate")} placeholder="Véhicule (immatriculation)" className={`${inp} uppercase`} />
      </div>
      {msg && <div data-testid="public-visit-error" className="text-sm text-rose-700 bg-rose-50 rounded-lg px-3 py-2">{msg}</div>}
      <button data-testid="public-visit-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#0B2A7A] hover:bg-[#123a9e] text-white font-bold rounded-lg py-3 transition-colors"><CalendarPlus className="h-4 w-4" />{edit ? "Enregistrer les modifications" : "Annoncer la visite"}</button>
    </form>
  );
}

export default function PublicVisitorAccess() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [fail, setFail] = useState("");
  const [edit, setEdit] = useState(null);
  const [ok, setOk] = useState("");
  const load = () => axios.get(`${API}/${token}`).then((r) => setData(r.data)).catch((e) => setFail(err(e)));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const cancel = async (x) => {
    if (!window.confirm(`Annuler la visite de ${x.name} le ${fr(x.date)} ?`)) return;
    try { await axios.delete(`${API}/${token}/${x.id}`); load(); } catch (e) { setOk(err(e)); }
  };
  if (fail) return <div className="min-h-screen grid place-items-center bg-slate-50 p-6 text-center text-slate-600" data-testid="public-visit-invalid">{fail}</div>;
  if (!data) return <div className="min-h-screen grid place-items-center text-slate-500">Chargement…</div>;
  const upcoming = data.items.filter((x) => x.date >= today());
  const past = data.items.filter((x) => x.date < today());
  const Row = ({ x }) => (
    <div className="px-4 py-3 flex items-center gap-3" data-testid={`public-visit-${x.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm">{x.name}{x.company && <span className="font-normal text-slate-500"> · {x.company}</span>}</div>
        <div className="text-xs text-slate-500">{fr(x.date)} · {x.time_from || x.time_to ? `${x.time_from || "…"} – ${x.time_to || "…"}` : "toute la journée"}{x.host && ` · ${x.host}`}{x.purpose && ` · ${x.purpose}`}</div>
      </div>
      {x.arrived_at ? (
        <span className="text-xs font-bold rounded px-2 py-1 bg-emerald-100 text-emerald-800 inline-flex items-center gap-1" data-testid={`public-visit-status-${x.id}`}><CheckCircle2 className="h-3.5 w-3.5" />Arrivé {hm(x.arrived_at)}{x.left_at && ` · parti ${hm(x.left_at)}`}</span>
      ) : x.date >= today() ? (
        <>
          <span className="text-xs font-semibold text-slate-500 inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Attendu</span>
          <button data-testid={`public-visit-edit-${x.id}`} onClick={() => setEdit(x)} className="p-1.5 rounded hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
          <button data-testid={`public-visit-cancel-${x.id}`} onClick={() => cancel(x)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 className="h-4 w-4" /></button>
        </>
      ) : <span className="text-xs text-slate-400">Non venu</span>}
    </div>
  );
  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-visitor-access">
      <header className="bg-[#0B1A4A] text-white">
        <div className="max-w-3xl mx-auto px-5 py-6">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#E8C46A] font-bold">{data.provider}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1">Visiteurs — {data.site_name}</h1>
          <p className="text-sm text-white/70 mt-1">Espace de {data.name}{data.company && ` (${data.company})`} · les agents de sécurité du site reçoivent vos annonces.</p>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-6 space-y-6">
        {ok && <div className="text-sm text-rose-700">{ok}</div>}
        <VisitForm key={edit?.id || "new"} token={token} edit={edit} onDone={() => { setEdit(null); load(); }} onCancel={() => setEdit(null)} />
        <section>
          <h2 className="font-display text-lg font-extrabold mb-2">Visites à venir <span className="text-sm text-slate-500">({upcoming.length})</span></h2>
          <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-testid="public-visits-upcoming">
            {upcoming.length ? upcoming.map((x) => <Row key={x.id} x={x} />) : <div className="p-5 text-sm text-slate-500 text-center">Aucune visite annoncée.</div>}
          </div>
        </section>
        {past.length > 0 && (
          <section>
            <h2 className="font-display text-lg font-extrabold mb-2 text-slate-600">Historique</h2>
            <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-testid="public-visits-past">{past.map((x) => <Row key={x.id} x={x} />)}</div>
          </section>
        )}
      </main>
    </div>
  );
}
